import React, { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    LayoutDashboard, FileText, Users, User, Car, Shield,
    BarChart3, Settings, LogOut, Menu, DollarSign, BookOpen, ChevronLeft, ChevronRight
} from 'lucide-react';
import AnimatedLogo from './AnimatedLogo';
import toast from 'react-hot-toast';

const navGroups = [
    {
        label: 'Navigation',
        items: [
            { icon: LayoutDashboard, label: 'Tableau de bord', path: '/' },
        ]
    },
    {
        label: 'Opérations',
        items: [
            { icon: Users, label: 'Dossiers Sinistrés', path: '/sinistres' },
            { icon: FileText, label: 'Rapports & PV', path: '/rapports' },
        ]
    },
    {
        label: 'Finances',
        items: [
            { icon: DollarSign, label: 'Honoraires', path: '/honoraires' },
        ]
    },
    {
        label: 'Annuaires',
        items: [
            { icon: Shield, label: 'Assurances', path: '/assurances' },
            { icon: Car, label: 'Catalogue de Véhicules', path: '/vehicules' },
            { icon: BookOpen, label: 'Catalogues Techniques', path: '/catalogues' },
        ]
    },
    {
        label: 'Statistiques',
        items: [
            { icon: BarChart3, label: 'Analyses', path: '/reporting' },
        ]
    }
];

const adminNav = {
    label: 'Administration',
    items: [
        { icon: Settings, label: 'Paramètre du système', path: '/parametres' },
    ]
};

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, isAdmin } = useAuth();
    const [showProfileMenu, setShowProfileMenu] = useState(false);

    const handleLogout = async () => {
        setShowProfileMenu(false);
        await logout();
        navigate('/login');
    };

    const groups = isAdmin
        ? [...navGroups, adminNav]
        : (user?.role === 'agent'
            ? navGroups.filter(g => ['Opérations', 'Annuaires'].includes(g.label))
            : navGroups);

    // 2-letter initials
    const getInitials = () => {
        const n = user?.nom || user?.username || 'U';
        return n.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    };

    return (
        <>
            {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
            <aside className={`sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
                {/* Header */}
                <div className="sidebar-header" style={{ position: 'relative' }}>
                    {!isCollapsed ? (
                        <div className="sidebar-logo">
                            <div style={{
                                backgroundColor: 'transparent',
                                padding: '10px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%'
                            }}>
                                <AnimatedLogo height="135px" />
                            </div>
                        </div>
                    ) : (
                        <div style={{ height: '70px' }} />
                    )}

                    {/* Desktop Toggle Button */}
                    <button
                        onClick={onToggleCollapse}
                        className="desktop-collapse-btn"
                        style={{
                            position: 'absolute',
                            right: '-14px',
                            top: '25px',
                            background: 'var(--primary-600)',
                            color: 'white',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'var(--shadow)',
                            zIndex: 101,
                            cursor: 'pointer',
                            border: '2px solid var(--surface-2)'
                        }}
                    >
                        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                    </button>
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                    {(Array.isArray(groups) ? groups : []).map((group) => (
                        <div key={group.label} className="nav-group">
                            <div className="nav-group-label">{group.label}</div>
                            {Array.isArray(group.items) && group.items.map((item) => {
                                const Icon = item.icon;
                                const isActive = item.path === '/'
                                    ? location.pathname === '/'
                                    : location.pathname.startsWith(item.path);
                                return (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`nav-item ${isActive ? 'active' : ''}`}
                                        onClick={onClose}
                                        title={isCollapsed ? item.label : undefined}
                                    >
                                        <Icon className="nav-icon" size={isCollapsed ? 20 : 16} />
                                        {!isCollapsed && <span className="nav-label">{item.label}</span>}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* Footer — click opens profile menu, NOT direct logout */}
                <div className="sidebar-footer" style={{ position: 'relative' }}>
                    {showProfileMenu && (
                        <>
                            <div
                                style={{ position: 'fixed', inset: 0, zIndex: 998 }}
                                onClick={() => setShowProfileMenu(false)}
                            />
                            <div style={{
                                position: 'absolute', bottom: '100%', left: '12px', right: '12px',
                                background: 'var(--primary-800)', borderRadius: '10px',
                                border: '1px solid rgba(255,255,255,0.12)',
                                boxShadow: '0 -8px 30px rgba(0,0,0,0.3)',
                                padding: '8px', zIndex: 999, marginBottom: '6px',
                            }}>
                                <div style={{ padding: '8px 12px 10px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '6px' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>{user?.nom || user?.username}</div>
                                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '1px' }}>
                                        {user?.role === 'admin' ? 'Administrateur' : user?.role === 'agent' ? 'Agent' : 'Expert'}
                                    </div>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    style={{
                                        width: '100%', padding: '9px 12px', background: 'rgba(239,68,68,0.12)',
                                        border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8px',
                                        color: '#fca5a5', cursor: 'pointer', display: 'flex',
                                        alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600',
                                    }}
                                >
                                    <LogOut size={14} /> Se déconnecter
                                </button>
                            </div>
                        </>
                    )}
                    <div
                        className="sidebar-user"
                        onClick={() => setShowProfileMenu(m => !m)}
                        title="Mon profil"
                        style={{ cursor: 'pointer' }}
                    >
                        <div className="user-avatar">{getInitials()}</div>
                        <div className="user-info">
                            <div className="user-name">{user?.nom || user?.username}</div>
                            <div className="user-role">
                                {user?.role === 'admin' ? 'Administrateur' : user?.role === 'agent' ? 'Agent (Limité)' : 'Expert'}
                            </div>
                        </div>
                        <LogOut size={14} color="rgba(255,255,255,0.5)" />
                    </div>
                </div>
            </aside>
        </>
    );
};

const Layout = ({ children, title, headerActions }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');
    const { isAdmin, user } = useAuth();

    const toggleCollapse = () => {
        const newState = !sidebarCollapsed;
        setSidebarCollapsed(newState);
        localStorage.setItem('sidebarCollapsed', newState);
    };
    const [activeUsers, setActiveUsers] = useState([]);
    const [showActiveUsers, setShowActiveUsers] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const prevUsersRef = React.useRef([]);
    const isFirstLoad = React.useRef(true);

    // Update date every minute
    React.useEffect(() => {
        const timer = setInterval(() => setCurrentDate(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    // Lock body scroll on mobile when sidebar is open
    React.useEffect(() => {
        if (sidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [sidebarOpen]);

    // Load active users if admin
    const loadActiveUsers = useCallback(async () => {
        if (!isAdmin) return;
        try {
            const { usersAPI } = await import('../services/api');
            const res = await usersAPI.getActive();
            const currentUsers = res.data.data || [];

            if (!isFirstLoad.current) {
                // Check for new connections
                currentUsers.forEach(u => {
                    const wasActive = prevUsersRef.current.some(prev => prev.id === u.id);
                    if (!wasActive && u.id !== user?.id) {
                        toast.success(`${u.nom || u.username} s'est connecté`, {
                            icon: '🟢',
                            duration: 4000,
                            style: { background: 'var(--primary-900)', color: '#fff', borderLeft: '4px solid #10b981' }
                        });
                    }
                });

                // Check for disconnections
                (Array.isArray(prevUsersRef.current) ? prevUsersRef.current : []).forEach(prev => {
                    const isActive = (Array.isArray(currentUsers) ? currentUsers : []).some(curr => curr.id === prev.id);
                    if (!isActive && prev.id !== user?.id) {
                        toast.error(`${prev.nom || prev.username} s'est déconnecté`, {
                            icon: '⚪',
                            duration: 4000,
                            style: { background: 'var(--primary-900)', color: '#fff', borderLeft: '4px solid #ef4444' }
                        });
                    }
                });
            }

            prevUsersRef.current = currentUsers;
            setActiveUsers(currentUsers);
            isFirstLoad.current = false;
        } catch (e) { console.error(e); }
    }, [isAdmin, user?.id]);

    React.useEffect(() => {
        loadActiveUsers();
        const interval = setInterval(loadActiveUsers, 10000); // 10s for more "real-time" feel
        return () => clearInterval(interval);
    }, [loadActiveUsers]);

    return (
        <div className="app-layout">
            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                isCollapsed={sidebarCollapsed}
                onToggleCollapse={toggleCollapse}
            />
            <main className={`main-content ${sidebarCollapsed ? 'collapsed' : ''}`}>
                <header className="topbar">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button className="menu-toggle" onClick={() => setSidebarOpen(true)}>
                            <Menu size={20} />
                        </button>
                        <h1 className="topbar-title">{title || 'HabibExpertiseSoft'}</h1>
                    </div>
                    <div className="topbar-actions">
                        {headerActions && <div className="header-custom-actions">{headerActions}</div>}
                        {isAdmin && (
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowActiveUsers(!showActiveUsers)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '4px',
                                        borderRadius: '8px',
                                        position: 'relative',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#10b981',
                                        transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.1)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    title={`${activeUsers.length} utilisateur${activeUsers.length !== 1 ? 's' : ''} connecté${activeUsers.length !== 1 ? 's' : ''}`}
                                    className="active-users-toggle"
                                >
                                    {activeUsers.length > 1
                                        ? <Users size={20} />
                                        : <User size={20} />
                                    }
                                    {/* Count badge */}
                                    <span style={{
                                        position: 'absolute',
                                        top: '-4px',
                                        right: '-4px',
                                        background: '#10b981',
                                        color: 'white',
                                        fontSize: '10px',
                                        fontWeight: '800',
                                        lineHeight: 1,
                                        padding: '2px 4px',
                                        borderRadius: '10px',
                                        minWidth: '16px',
                                        textAlign: 'center',
                                        boxShadow: '0 1px 4px rgba(16,185,129,0.4)',
                                    }}>
                                        {activeUsers.length}
                                    </span>
                                </button>
                                {showActiveUsers && (
                                    <>
                                        <div
                                            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
                                            onClick={() => setShowActiveUsers(false)}
                                        />
                                        <div className="card" style={{
                                            position: 'absolute',
                                            top: '100%',
                                            right: 0,
                                            marginTop: '8px',
                                            width: '240px',
                                            zIndex: 999,
                                            padding: '12px',
                                            boxShadow: 'var(--shadow-lg)',
                                            maxHeight: '300px',
                                            overflowY: 'auto'
                                        }}>
                                            <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '8px', borderBottom: '1px solid var(--border)', paddingBottom: '4px' }}>
                                                👤 Actuellement en ligne
                                            </div>
                                            {activeUsers.length === 0 ? (
                                                <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '8px 0' }}>Aucun utilisateur</div>
                                            ) : (
                                                Array.isArray(activeUsers) && activeUsers.map(u => (
                                                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid var(--surface-2)' }}>
                                                        <div style={{ width: '6px', height: '6px', background: '#10b981', borderRadius: '50%' }} />
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontSize: '12px', fontWeight: '600' }}>{u.nom || u.username}</div>
                                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                                                {u.role === 'admin' ? 'Administrateur' : u.role === 'agent' ? 'Agent' : 'Expert'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }} className="topbar-date">
                            {currentDate.toLocaleDateString('fr-DZ', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                    </div>
                </header>
                <div className="page-content">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
